import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { clientsService, quotesService } from '../services/api';
import { ClientModal } from '../components/ClientModal';
import { QuoteModal } from '../components/QuoteModal';
import { formatApptDateLong, formatApptTime } from '../utils/dateFormatters';
import { formatMoney } from '../utils/pricing';
import './ClientDetail.css';

const STATUS_LABEL = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [quoteModal, setQuoteModal] = useState(null); // { quote } | { prefill }

  async function load() {
    setLoading(true);
    const [c, a, q] = await Promise.all([
      clientsService.getById(id), clientsService.getAppointments(id), quotesService.getAll({ customerId: id })
    ]);
    if (c.success) setClient(c.customer);
    if (a.success) setAppointments(a.appointments || []);
    if (q.success) setQuotes(q.quotes || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, [id]);

  // Deleting is only offered once the client has no appointments or quotes —
  // those would otherwise be left behind with no client attached.
  const blocked = appointments.length > 0 || quotes.length > 0;
  async function remove() {
    if (blocked || deleting) return;
    if (!window.confirm(`Delete ${client.name}? This removes their contact record. It cannot be undone.`)) return;
    setDeleting(true); setDeleteError('');
    const r = await clientsService.delete(id);
    setDeleting(false);
    if (r.success) navigate('/clients');
    else setDeleteError(r.error || 'Could not delete this client');
  }

  if (loading) return <div className="empty-state"><div className="loading-spinner"></div></div>;
  if (!client) return <div className="empty-state"><p>Client not found</p><Link to="/clients" className="btn btn-outline">Back to clients</Link></div>;

  const addr = [client.address_street, client.address_suburb, client.address_state, client.address_postcode].filter(Boolean).join(', ');

  return (
    <div className="client-detail">
      <div className="page-header">
        <div>
          <Link to="/clients" className="back-link">← Clients</Link>
          <h1 className="page-title">{client.name}</h1>
        </div>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={() => setQuoteModal({ prefill: { customer_id: Number(id) } })}>+ Quote</button>
          {!client.anonymised_at && <button className="btn btn-outline" onClick={() => setEditing(true)}>Edit</button>}
          <button className="btn btn-danger" onClick={remove} disabled={blocked || deleting}
            title={blocked ? 'Delete their appointments and quotes first' : 'Delete this client'}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
      {blocked && <p className="text-muted client-delete-hint">To delete this client, first delete their appointments and quotes.</p>}
      {deleteError && <p className="form-error client-delete-hint">{deleteError}</p>}

      <div className="client-detail-grid">
        <section className="card">
          <h2 className="card-title mb-md">Contact</h2>
          {client.anonymised_at ? (
            <p className="text-muted">Anonymised on {formatApptDateLong(client.anonymised_at)} — details removed under the retention setting; their jobs and quotes are still here.</p>
          ) : (
            <>
              {client.phone && <p><a className="click-to-call" href={`tel:${client.phone}`}>{client.phone}</a></p>}
              {client.email && <p><a className="click-to-call" href={`mailto:${client.email}`}>{client.email}</a></p>}
              {addr && <p className="text-muted mt-sm">{addr}</p>}
              {client.notes && <p className="mt-md">{client.notes}</p>}
            </>
          )}
        </section>

        <section className="card">
          <h2 className="card-title mb-md">Appointments ({appointments.length})</h2>
          {appointments.length === 0 ? <p className="text-muted">No appointments yet.</p> : (
            <div className="client-appt-list">
              {appointments.map((a) => (
                <div key={a.id} className="client-appt">
                  <div className="client-appt-when">
                    <strong>{formatApptDateLong(a.starts_at)}</strong>
                    <span className="text-muted">{formatApptTime(a.starts_at)}</span>
                  </div>
                  <span>{a.service_name} · {a.crew_names || a.staff_name}</span>
                  <span className={`status-badge status-${a.status.replace('_', '-')}`}>{a.status.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <h2 className="card-title mb-md">Quotes ({quotes.length})</h2>
          {quotes.length === 0 ? <p className="text-muted">No quotes yet.</p> : (
            <div className="client-appt-list">
              {quotes.map((q) => (
                <button key={q.id} type="button" className="client-appt client-quote" onClick={() => setQuoteModal({ quote: q })}>
                  <div className="client-appt-when">
                    <strong>{q.quote_number}</strong>
                    <span className="text-muted">{formatApptDateLong(q.sent_at || q.created_at)}</span>
                  </div>
                  <span>{q.title || `${q.item_count} line item${Number(q.item_count) === 1 ? '' : 's'}`} · {formatMoney(q.total)}</span>
                  <span className={`status-badge status-${q.effective_status}`}>{STATUS_LABEL[q.effective_status] || q.effective_status}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <ClientModal isOpen={editing} onClose={() => setEditing(false)} onSave={() => { setEditing(false); load(); }} client={client} />
      <QuoteModal
        isOpen={!!quoteModal}
        onClose={() => { setQuoteModal(null); load(); }}
        onSave={() => {}}
        quote={quoteModal?.quote || null}
        prefill={quoteModal?.prefill || null}
      />
    </div>
  );
}
